/**
 * RequestClubVisit
 * Component voor het aanvragen van een bezoek aan een netwerkclub
 */

import React, { useState } from 'react';
import { auth, db } from '../../../firebase';
import { collection, addDoc, serverTimestamp, query, where, getDocs } from 'firebase/firestore';
import { ClubVisitRequest } from '../../../types/networkclub';
import { Icon } from '../../icons';
import { Calendar, MapPin, Building2, User, Mail, Phone, FileText, Send, Loader, X } from '../../icons';

interface RequestClubVisitProps {
  clubId: string;
  clubName: string;
  entityId: string;
  onClose: () => void;
  onSuccess: () => void;
}

const RequestClubVisit: React.FC<RequestClubVisitProps> = ({ 
  clubId, 
  clubName, 
  entityId,
  onClose, 
  onSuccess 
}) => {
  const [formData, setFormData] = useState({
    visitDate: '',
    visitTime: '',
    visitPurpose: '',
    notes: '',
    company: '',
    phone: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    
    // Validation
    if (!formData.visitDate) {
      setError('Bezoekdatum is verplicht');
      return;
    }
    if (!formData.visitTime) {
      setError('Bezoektijd is verplicht');
      return;
    }
    if (!formData.visitPurpose) {
      setError('Doel van bezoek is verplicht');
      return;
    }

    setLoading(true);
    try {
      const user = auth.currentUser;
      if (!user) {
        setError('Je moet ingelogd zijn');
        setLoading(false);
        return;
      }

      // Check of er al een openstaand verzoek is
      const existingRequestsQuery = query(
        collection(db, 'clubVisitRequests'),
        where('clubId', '==', clubId),
        where('visitorId', '==', user.uid),
        where('status', '==', 'pending')
      );
      const existingSnap = await getDocs(existingRequestsQuery);
      
      if (!existingSnap.empty) {
        setError('Je hebt al een openstaand bezoekverzoek voor deze club');
        setLoading(false);
        return;
      }

      // Haal user data op
      const userDoc = await getDocs(query(collection(db, 'users'), where('uid', '==', user.uid)));
      const userData = userDoc.empty ? {} : userDoc.docs[0].data();

      // Maak bezoekverzoek aan
      const visitDate = new Date(`${formData.visitDate}T${formData.visitTime}`);
      const visitRequest: Omit<ClubVisitRequest, 'id' | 'createdAt' | 'respondedAt'> = {
        clubId,
        entityId,
        visitorId: user.uid,
        visitorName: user.displayName || userData.displayName || user.email?.split('@')[0] || 'Onbekend',
        visitorEmail: user.email || userData.email || '',
        visitDate,
        purpose: formData.visitPurpose,
        message: formData.notes.trim() || undefined,
        company: formData.company.trim() || undefined,
        phone: formData.phone.trim() || undefined,
        status: 'pending'
      };

      const requestRef = await addDoc(collection(db, 'clubVisitRequests'), {
        ...visitRequest,
        createdAt: serverTimestamp()
      });

      // Maak inbox item aan voor beheerders
      const ownersQuery = query(
        collection(db, 'entityMembers'),
        where('entityId', '==', entityId),
        where('role', 'in', ['owner', 'organizer'])
      );
      const ownersSnap = await getDocs(ownersQuery);

      for (const ownerDoc of ownersSnap.docs) {
        const ownerData = ownerDoc.data();
        await addDoc(collection(db, 'inboxItems'), {
          userId: ownerData.userId,
          type: 'notification',
          category: 'planning',
          title: '🏢 Bezoekverzoek voor ' + clubName,
          message: `${visitRequest.visitorName} wil de club bezoeken op ${visitDate.toLocaleDateString('nl-NL')} om ${visitDate.toLocaleTimeString('nl-NL', { hour: '2-digit', minute: '2-digit' })}`,
          subtitle: `Doel: ${formData.visitPurpose}`,
          isRead: false,
          priority: 'medium',
          createdAt: serverTimestamp(),
          actionData: {
            actionType: 'accept_invitation',
            actionText: 'Bekijk Verzoek',
            visitRequestId: requestRef.id,
            clubId: clubId
          },
          metadata: {
            visitDate: visitDate,
            visitPurpose: formData.visitPurpose,
            visitorName: visitRequest.visitorName,
            visitorEmail: visitRequest.visitorEmail
          }
        });
      }

      if (requestRef.id) {
        onSuccess();
      } else {
        setError('Kon bezoekverzoek niet aanmaken');
      }
    } catch (err: any) {
      console.error('Error creating visit request:', err);
      setError(err.message || 'Fout bij aanmaken van bezoekverzoek');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-md">
        {/* Header */}
        <div className="bg-gradient-to-r from-orange-500 to-amber-500 p-5 text-white rounded-t-xl">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center">
                <Icon icon={Building2} size={20} strokeWidth={1.5} />
              </div>
              <div>
                <h3 className="text-xl font-bold">Club bezoeken</h3>
                <p className="text-white/80 text-sm">{clubName}</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-white/20 rounded-lg transition-colors"
            >
              <Icon icon={X} size={20} strokeWidth={1.5} />
            </button>
          </div>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {error && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
              {error}
            </div>
          )}

          {/* Bezoekdatum */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Bezoekdatum <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Icon icon={Calendar} size={16} strokeWidth={1.5} color="#6b7280" />
              </div>
              <input
                type="date"
                value={formData.visitDate}
                onChange={(e) => setFormData({ ...formData, visitDate: e.target.value })}
                className="w-full pl-10 pr-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
                min={new Date().toISOString().split('T')[0]}
                required
              />
            </div>
          </div>

          {/* Bezoektijd */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Bezoektijd <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Icon icon={Calendar} size={16} strokeWidth={1.5} color="#6b7280" />
              </div>
              <input
                type="time"
                value={formData.visitTime}
                onChange={(e) => setFormData({ ...formData, visitTime: e.target.value })}
                className="w-full pl-10 pr-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
                required
              />
            </div>
          </div>

          {/* Doel van bezoek */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Doel van bezoek <span className="text-red-500">*</span>
            </label>
            <select
              value={formData.visitPurpose}
              onChange={(e) => setFormData({ ...formData, visitPurpose: e.target.value })}
              className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
              required
            >
              <option value="">Selecteer...</option>
              <option value="Kennismaking">Kennismaking</option>
              <option value="Lid worden">Lid worden</option>
              <option value="Event bijwonen">Event bijwonen</option>
              <option value="Samenwerking">Samenwerking</option>
              <option value="Anders">Anders</option>
            </select>
          </div>

          {/* Bedrijf */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Bedrijf
            </label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Icon icon={Building2} size={16} strokeWidth={1.5} color="#6b7280" />
              </div>
              <input
                type="text"
                value={formData.company}
                onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                className="w-full pl-10 pr-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
                placeholder="Bedrijfsnaam"
              />
            </div>
          </div>

          {/* Telefoon */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Telefoon
            </label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Icon icon={Phone} size={16} strokeWidth={1.5} color="#6b7280" />
              </div>
              <input
                type="tel"
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                className="w-full pl-10 pr-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
                placeholder="+31 6 12345678"
              />
            </div>
          </div>

          {/* Notities */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Notities
            </label>
            <div className="relative">
              <div className="absolute top-2 left-3 pointer-events-none">
                <Icon icon={FileText} size={16} strokeWidth={1.5} color="#6b7280" />
              </div>
              <textarea
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                className="w-full pl-10 pr-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 resize-none"
                rows={3}
                placeholder="Optionele notities over je bezoek..."
              />
            </div>
          </div>

          {/* Buttons */}
          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors font-medium"
              disabled={loading}
            >
              Annuleren
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex-1 px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors font-medium flex items-center justify-center space-x-2"
            >
              {loading ? (
                <>
                  <Icon icon={Loader} size={16} strokeWidth={1.5} className="animate-spin" />
                  <span>Verzenden...</span>
                </>
              ) : (
                <>
                  <Icon icon={Send} size={16} strokeWidth={1.5} />
                  <span>Verzoek indienen</span>
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default RequestClubVisit;
